import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FavoriteController } from 'api';
import message from '../components/Message';

export type UserFavoritesContextType = {
  favorites: Set<string>;
  toggleFavorite: (listingId: string) => Promise<void>;
  getFavorites: () => Set<string>;
};

const UserFavoritesContext = createContext<UserFavoritesContextType>({} as any);

export default UserFavoritesContext;

export const UserFavoritesContextProvider: React.FC = ({ children }) => {
  const [userFavorites, setUserFavorites] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchUserFavorites = async () => {
      const fetchedUserFavorites = await FavoriteController.list();
      setUserFavorites(new Set(fetchedUserFavorites));
    };

    fetchUserFavorites();
  }, []);

  const toggleFavorite = useCallback(
    async (listingId: string) => {
      const result = await FavoriteController.toggle(listingId);
      if (result.exists) {
        userFavorites.delete(listingId);
        message.success('Removed from favorites!');
      } else {
        userFavorites.add(listingId);
        message.success('Added to favorites!');
      }
      setUserFavorites(new Set(userFavorites));
    },
    [userFavorites],
  );

  const getFavorites = useCallback(() => userFavorites, [userFavorites]);

  const value = useMemo(
    () => ({
      favorites: new Set(userFavorites),
      toggleFavorite,
      getFavorites,
    }),
    [userFavorites, toggleFavorite, getFavorites],
  );

  return (
    <UserFavoritesContext.Provider value={value}>
      {children}
    </UserFavoritesContext.Provider>
  );
};
